/**
 * StatusBadge — per-stage review-state pill (failed / continued-with-warnings /
 * edited-approved / approved). Lives in its own file so StageHistoryRowHelpers
 * can stay a pure-function module (react-refresh only-export-components).
 */
import { CheckCircle2, AlertCircle, AlertTriangle, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { AgentResult } from '@/types';

export function StatusBadge({ result }: { result: AgentResult }) {
  if (result.status === 'failed') {
    return (
      <Badge variant="destructive" className="text-xs gap-1">
        <AlertCircle className="h-3 w-3" aria-hidden="true" />
        Failed
      </Badge>
    );
  }
  // "Continue with warnings" on a gate is recorded as edited_approved with a
  // continued_with_warnings flag; surface it distinctly from a clean approval.
  if ((result.edits_json)?.continued_with_warnings) {
    return (
      <Badge variant="outline" className="text-xs gap-1 bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800">
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        Continued w/ warnings
      </Badge>
    );
  }
  // Edited approvals are coloured differently from clean approvals so
  // the reviewer can spot stages that previously needed a note before
  // opening them.
  if (result.review_state === 'edited_approved') {
    return (
      <Badge variant="outline" className="text-xs gap-1 bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-800">
        <Pencil className="h-3 w-3" aria-hidden="true" />
        Edited &amp; approved
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs gap-1 bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800">
      <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
      Approved
    </Badge>
  );
}
